/**
 * 🎯 Dedicated Event Points Ledger API Client for Zeta Admin
 * Specifications from ADMIN_GAMES_AND_EVENT_POINTS_README.md (Section 4.2)
 */

const getAdminBaseUrl = () => {
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:18009/admin";
  const trimmed = rawBase.replace(/\/+$/, "");
  return trimmed.endsWith("/admin") ? trimmed : `${trimmed}/admin`;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("adminToken");
  if (!token) {
    return { "Content-Type": "application/json" };
  }
  const authHeader =
    token.startsWith("Basic ") || token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  return {
    "Content-Type": "application/json",
    Authorization: authHeader,
  };
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok || (typeof result === "object" && result.status === false)) {
    const errorMsg =
      (typeof result === "object" &&
        (result?.error?.message ||
          result?.error ||
          result?.sqlMessage ||
          result?.message ||
          result?.details)) ||
      (typeof result === "string" ? result : "API request failed");
    throw new Error(errorMsg);
  }

  return result;
};

/**
 * Fetch Event Points across games with per-game summary cards and history
 * Endpoint: GET /admin/game/event-points (or /admin/game/event-points/:userId)
 * @param {Object} params
 * @param {string|number} [params.userId]
 * @param {string} [params.gameKey] - "all" | "game_2048" | "fruit_Bomb" | "tap" | "boomcards" | "slots" | "spinwheel"
 * @param {string|number} [params.eventId]
 * @param {number} [params.page=1]
 * @param {number} [params.perPage=20]
 * @param {string} [params.search=""]
 */
export const getEventPointsLedger = async ({
  userId = "",
  gameKey = "all",
  eventId = "",
  page = 1,
  perPage = 20,
  search = "",
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (userId && String(userId).trim()) {
    queryParams.append("userId", String(userId).trim());
  }
  if (gameKey && gameKey !== "all") {
    queryParams.append("gameKey", String(gameKey).trim());
  }
  if (eventId && eventId !== "all" && String(eventId).trim()) {
    queryParams.append("eventId", String(eventId).trim());
  }
  if (page) {
    queryParams.append("page", String(page));
  }
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) {
    queryParams.append("search", search.trim());
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

  // Endpoints to attempt
  const endpoints = [];
  if (userId && String(userId).trim()) {
    endpoints.push(`${baseUrl}/game/event-points/${encodeURIComponent(String(userId).trim())}${queryString}`);
  }
  endpoints.push(`${baseUrl}/game/event-points${queryString}`);
  endpoints.push(`${baseUrl}/events/points-ledger${queryString}`);

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error fetching Event Points Ledger");
      }
    }
  }

  return handleApiResponse(lastResponse);
};
